import React, { useEffect, useRef, useState } from "react";

import {
  isNullOrUndefined,
  isValidNonnegativeNumber,
  isWithinBounds,
  queryIsLoading,
  useDairyMethodology,
  useGetFootprintingDairyGrasslandFertiliser,
  useGetFootprintingDairyHerdSize,
  useGetFootprintingDairyPrimaryDataById,
  useGetPracticeMineralFertilizers,
  useGetPracticeOrganicFertilizers,
  useKeyedFormList,
  useMutateFootprintingDairyGrasslandFertiliser,
  useMutateFootprintingDairyPrimaryData,
  useTranslation,
} from "utils";

import {
  DAIRY_GRASSLAND_FERTILISER_ACTIVE,
  ENUM_DAIRY_GRASSLAND_FERTILISER_ACTIVE,
  ENUM_FARM_FOOTPRINTING_DAIRY_COWS_IN_PASTURE,
  ENUM_FERTILIZER_UNITS,
  FARM_FOOTPRINTING_DAIRY_COWS_IN_PASTURE,
  PRACTICE_SLUG,
  UploadableGrasslandFertiliser,
  ZERO_UUID,
} from "models";

import {
  AlertBanner,
  Button,
  Card,
  Flex,
  Form,
  FormInstance,
  Icon,
  IconButton,
  InputNumber,
  InputNumberWithUnitSelector,
  KlimRadioGroup,
  Option,
  PageSpin,
  Select,
  Typography,
} from "components";

type PastureTimeBannerState = {
  isTooLow: boolean;
  isInRange: boolean;
  isTooHigh: boolean;
};

const PastureTimeThresholdAlertContent = ({ variant }: { variant: "high" | "low" }) => {
  const { t } = useTranslation("footprintingDairy");
  const keyPrefix = `dairyFeedPastureForm.error.pastureTimeDescription${variant === "high" ? "High" : "Low"}`;
  const bulletsRaw = t(`${keyPrefix}.bullets`, { returnObjects: true });
  const bulletItems = Array.isArray(bulletsRaw) ? (bulletsRaw as string[]) : [];

  return (
    <Flex vertical className="feed-pasture-alert-description">
      <Typography variant="paragraph-m" className="feed-pasture-alert-description__paragraph">
        {t(`${keyPrefix}.intro`)}
      </Typography>
      <Typography variant="paragraph-m" className="feed-pasture-alert-description__paragraph">
        {t(`${keyPrefix}.context`)}
      </Typography>
      <Typography variant="paragraph-m" className="feed-pasture-alert-description__review-prompt">
        {t(`${keyPrefix}.reviewPrompt`)}
      </Typography>
      {bulletItems.length > 0 ? (
        <ul className="feed-pasture-alert-description__list">
          {bulletItems.map((bulletText, index) => (
            <li key={`${keyPrefix}-bullet-${index}`}>
              <Typography variant="paragraph-m">{bulletText}</Typography>
            </li>
          ))}
        </ul>
      ) : null}
      <Typography variant="paragraph-m" className="feed-pasture-alert-description__paragraph">
        {t(`${keyPrefix}.closing`)}
      </Typography>
    </Flex>
  );
};

const PastureTimeAlertDescription = ({ pastureTime }: { pastureTime: PastureTimeBannerState }) => {
  const { t } = useTranslation("footprintingDairy");

  if (pastureTime.isTooHigh) {
    return <PastureTimeThresholdAlertContent variant="high" />;
  }
  if (pastureTime.isTooLow) {
    return <PastureTimeThresholdAlertContent variant="low" />;
  }
  if (pastureTime.isInRange) {
    return t("dairyFeedPastureForm.success.pastureTimeDescriptionInRange");
  }

  return null;
};

type DairyPastureProps = {
  year: number;
  farmId: string;
  primaryDataId?: string;
  onComplete?: () => void;
  readOnly?: boolean;
};

const grasslandFertiliserPlaceholder: UploadableGrasslandFertiliser = {
  id: undefined,
  fertiliserProductId: "",
  totalAmountApplied: null,
  totalAmountUnit: null,
};

const FeedPastureForm = ({ primaryDataId, readOnly, farmId, year }: DairyPastureProps) => {
  const { t } = useTranslation(["footprintingDairy", "practices"]);
  const { isCfp } = useDairyMethodology(farmId);
  const formRef = useRef<FormInstance>(null);
  const grasslandFormRef = useRef<FormInstance>(null);

  const [cowsInPastureActive, setCowsInPastureActive] = useState<ENUM_FARM_FOOTPRINTING_DAIRY_COWS_IN_PASTURE | null>(null);
  const [cowsInPasture, setCowsInPasture] = useState<number | null>(null);
  const [daysInPasturePerYear, setDaysInPasturePerYear] = useState<number | null>(null);
  const [avgHoursInPasturePerDay, setAvgHoursInPasturePerDay] = useState<number | null>(null);
  const [totalHerdSize, setTotalHerdSize] = useState<number>(0);

  const [grasslandFertiliserActive, setGrasslandFertiliserActive] = useState<ENUM_DAIRY_GRASSLAND_FERTILISER_ACTIVE | null>(null);

  const totalTimeInPasture = (daysInPasturePerYear ?? 0) * (avgHoursInPasturePerDay ?? 0);

  const dairyPrimaryDataFullQuery = useGetFootprintingDairyPrimaryDataById(primaryDataId ?? "");
  const { data: dairyPrimaryDataQuery } = dairyPrimaryDataFullQuery;
  const { data: herdSize } = useGetFootprintingDairyHerdSize(primaryDataId || "", { enabled: !!primaryDataId });
  const grasslandFertilisersFullQuery = useGetFootprintingDairyGrasslandFertiliser(primaryDataId);
  const { data: grasslandFertilisersQuery } = grasslandFertilisersFullQuery;
  const mineralFertilizerDataQuery = useGetPracticeMineralFertilizers({ enabled: true });
  const organicFertilizerDataQuery = useGetPracticeOrganicFertilizers({ enabled: true });
  const isLoading = [dairyPrimaryDataFullQuery, grasslandFertilisersFullQuery].some(queryIsLoading);

  // The grassland form only renders for CFP programs
  const keyedGrasslandFertilisers = useKeyedFormList<UploadableGrasslandFertiliser>(grasslandFormRef, { isReady: isCfp && !isLoading });

  const { mutate: mutatePastureData } = useMutateFootprintingDairyPrimaryData(
    farmId,
    year,
    {
      farmId,
      year,
      cowsInPastureActive,
      cowsInPasture,
      daysInPasturePerYear,
      avgHoursInPasturePerDay,
      grasslandFertiliserActive,
    },
    primaryDataId ?? ""
  );

  const { mutate: mutateGrasslandFertilisers } = useMutateFootprintingDairyGrasslandFertiliser(
    farmId,
    year,
    {
      selectedGrasslandFertilisers: keyedGrasslandFertilisers.payload.filter((f) => !!f.fertiliserProductId),
    },
    primaryDataId
  );

  //   Save on unmount
  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutatePastureData({});
        if (isCfp) mutateGrasslandFertilisers();
      }
    },
    [isCfp, isLoading, mutatePastureData, mutateGrasslandFertilisers, readOnly]
  );

  useEffect(() => {
    const total = herdSize?.reduce((sum, herd) => sum + (herd.cowCount || 0), 0) ?? 0;
    setTotalHerdSize(total);
  }, [herdSize]);

  //   Load existing data from sold cows table
  useEffect(() => {
    if (dairyPrimaryDataQuery) {
      setCowsInPastureActive(dairyPrimaryDataQuery.cowsInPastureActive ?? null);
      setCowsInPasture(dairyPrimaryDataQuery.cowsInPasture ?? null);
      setDaysInPasturePerYear(dairyPrimaryDataQuery.daysInPasturePerYear ?? null);
      setAvgHoursInPasturePerDay(dairyPrimaryDataQuery.avgHoursInPasturePerDay ?? null);
      setGrasslandFertiliserActive(dairyPrimaryDataQuery.grasslandFertiliserActive ?? null);

      formRef.current?.setFieldsValue({
        cowsInPasture: dairyPrimaryDataQuery.cowsInPasture ?? null,
        daysInPasturePerYear: dairyPrimaryDataQuery.daysInPasturePerYear ?? null,
        avgHoursInPasturePerDay: dairyPrimaryDataQuery.avgHoursInPasturePerDay ?? null,
      });

      const fieldsToValidate = (["cowsInPasture", "daysInPasturePerYear", "avgHoursInPasturePerDay"] as const).some(
        (field) => dairyPrimaryDataQuery[field] !== null && dairyPrimaryDataQuery[field] !== undefined
      );

      if (fieldsToValidate && formRef.current) {
        formRef.current?.validateFields();
      }

      // Only a saved "yes" shows entries to check; validating on a saved "no" could fire after the user has already switched to "yes"
      if (dairyPrimaryDataQuery.grasslandFertiliserActive === DAIRY_GRASSLAND_FERTILISER_ACTIVE.YES) {
        keyedGrasslandFertilisers.actions.requestRehydrationValidation();
      }
    }
  }, [dairyPrimaryDataQuery, keyedGrasslandFertilisers.actions]);

  // Load saved grassland fertilisers
  useEffect(() => {
    if (grasslandFertilisersQuery) {
      keyedGrasslandFertilisers.actions.hydrate(
        grasslandFertilisersQuery.length
          ? grasslandFertilisersQuery.map((f) => ({
              id: f.id,
              fertiliserProductId: f.fertiliserProductId ?? "",
              totalAmountApplied: f.totalAmountApplied ?? null,
              totalAmountUnit: f.totalAmountUnit ?? null,
            }))
          : [grasslandFertiliserPlaceholder]
      );
    }
  }, [grasslandFertilisersQuery, keyedGrasslandFertilisers.actions]);

  const pastureTime = {
    isTooLow: totalTimeInPasture > 0 && totalTimeInPasture < 700,
    isInRange: totalTimeInPasture >= 700 && totalTimeInPasture <= 2000,
    isTooHigh: totalTimeInPasture > 2000,
  };

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <Flex vertical gap={16}>
      <Card border className="padding-200">
        <Flex vertical gap={16}>
          <Flex align="center" gap={16}>
            <Typography variant="paragraph-m" className="flex-1">
              {t("dairyFeedPastureForm.cowPastureRadioQuestion")}
              <span className="required-asterisk">*</span>
            </Typography>
            <KlimRadioGroup
              required
              vertical={false}
              disabled={readOnly}
              type="tertiary"
              size="large"
              dataTestId="footprinting-dairy-herd-cow-weight-radio"
              onChange={({ target }) => {
                setCowsInPastureActive(target.value);
              }}
              value={cowsInPastureActive}
              optionType="button"
              options={[FARM_FOOTPRINTING_DAIRY_COWS_IN_PASTURE.YES, FARM_FOOTPRINTING_DAIRY_COWS_IN_PASTURE.NO].map((cowsInPastureActive) => ({
                label: t(`dairyFeedPastureForm.cowPastureOptions.${cowsInPastureActive}`),
                value: cowsInPastureActive,
              }))}
            />
          </Flex>
          {cowsInPastureActive === FARM_FOOTPRINTING_DAIRY_COWS_IN_PASTURE.YES && (
            <Form ref={formRef}>
              <Flex vertical gap={16}>
                <InputNumber
                  disabled={readOnly}
                  name="cowsInPasture"
                  dataTestId="footprinting-feed-cows-in-pasture-input"
                  label={t("dairyFeedPastureForm.question.cowsInPastureInput")}
                  suffix={t("dairyFeedPastureForm.question.cowsInPastureSuffix")}
                  value={cowsInPasture}
                  initialValue={cowsInPasture}
                  onChange={(v) => setCowsInPasture(v)}
                  validateFirst
                  rules={[
                    { required: true, message: t("dairyFeedPastureForm.error.validNumberCows") },
                    { type: "number" },
                    {
                      validator: (_: unknown, v: number) => {
                        if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                          return Promise.reject(new Error(t("dairyFeedPastureForm.error.validNumberCows")));
                        }
                        if (isNullOrUndefined(v) || !isWithinBounds(v, 0, totalHerdSize)) {
                          return Promise.reject(new Error(t("dairyFeedPastureForm.error.validNumberCowsInHerd", { totalHerdSize })));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                />

                <InputNumber
                  disabled={readOnly}
                  name="daysInPasturePerYear"
                  dataTestId="footprinting-feed-days-in-pasture-input"
                  label={t("dairyFeedPastureForm.question.daysInPastureInput")}
                  suffix={t("dairyFeedPastureForm.question.daysInPastureSuffix")}
                  value={daysInPasturePerYear}
                  initialValue={daysInPasturePerYear}
                  onChange={(v) => setDaysInPasturePerYear(v)}
                  validateFirst
                  rules={[
                    { required: true, message: t("dairyFeedPastureForm.error.validNumberDays") },
                    { type: "number" },
                    {
                      validator: (_: unknown, v: number) => {
                        if (isNullOrUndefined(v) || !isWithinBounds(v, 0, 365)) {
                          return Promise.reject(new Error(t("dairyFeedPastureForm.error.validNumberDays")));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                />

                <InputNumber
                  disabled={readOnly}
                  name="avgHoursInPasturePerDay"
                  dataTestId="footprinting-feed-time-in-pasture-input"
                  label={t("dairyFeedPastureForm.question.avgHoursInPastureInput")}
                  suffix={t("dairyFeedPastureForm.question.avgHoursInPastureSuffix")}
                  value={avgHoursInPasturePerDay}
                  initialValue={avgHoursInPasturePerDay}
                  onChange={(v) => setAvgHoursInPasturePerDay(v)}
                  validateFirst
                  rules={[
                    { required: true, message: t("dairyFeedPastureForm.error.validNumberHours") },
                    { type: "number" },
                    {
                      validator: (_: unknown, v: number) => {
                        if (isNullOrUndefined(v) || !isWithinBounds(v, 0, 24)) {
                          return Promise.reject(new Error(t("dairyFeedPastureForm.error.validNumberHours")));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                />
                <Typography variant="paragraph-s" color="grey-600" className="margin-top-25">
                  {t("dairyFeedPastureForm.estimationText")}
                </Typography>

                {(pastureTime.isTooHigh || pastureTime.isTooLow || pastureTime.isInRange) && (
                  <AlertBanner
                    type={pastureTime.isInRange ? "success" : "warning"}
                    iconName={pastureTime.isInRange ? "tabler/confetti" : "tabler/alert-triangle"}
                    iconAlign="start"
                    iconSize={24}
                    headingSize="h6"
                    title={
                      pastureTime.isTooHigh
                        ? t("dairyFeedPastureForm.error.pastureTimeTooHigh")
                        : pastureTime.isTooLow
                        ? t("dairyFeedPastureForm.error.pastureTimeTooLow")
                        : t("dairyFeedPastureForm.success.pastureTimeInRange")
                    }
                    description={<PastureTimeAlertDescription pastureTime={pastureTime} />}
                  />
                )}

                <Flex className="dairy-display-information-card flex-center padding-100">
                  <Typography variant="paragraph-s" color="grey-600" className="margin-bottom-25">
                    {t("dairyFeedPastureForm.feedPastureDisplayTitle")}
                  </Typography>
                  <Typography variant="h5" className="center">
                    {totalTimeInPasture} {t("dairyFeedPastureForm.feedPastureDisplayUnit")}
                  </Typography>
                </Flex>
              </Flex>
            </Form>
          )}
        </Flex>
      </Card>

      {isCfp && (
        <Card border className="padding-200" dataTestId="footprinting-dairy-grassland-fertiliser-form">
          <Form ref={grasslandFormRef} preserve={false}>
            <Flex vertical gap={16}>
              <Flex align="center" gap={16} justify="space-between">
                <Typography variant="paragraph-m" style={{ maxWidth: "60%" }}>
                  {t("dairyFeedPastureForm.grasslandFertiliser.question")}
                  <span className="required-asterisk">*</span>
                </Typography>
                <KlimRadioGroup
                  required
                  name="grasslandFertiliserActive"
                  dataTestId="footprinting-dairy-grassland-fertiliser-radio"
                  disabled={readOnly}
                  vertical={false}
                  type="tertiary"
                  size="large"
                  onChange={({ target }) => {
                    setGrasslandFertiliserActive(target.value);
                    if (target.value === DAIRY_GRASSLAND_FERTILISER_ACTIVE.YES && keyedGrasslandFertilisers.entries.length === 0) {
                      keyedGrasslandFertilisers.actions.add(grasslandFertiliserPlaceholder);
                    }
                  }}
                  value={grasslandFertiliserActive}
                  defaultValue={grasslandFertiliserActive}
                  optionType="button"
                  options={[DAIRY_GRASSLAND_FERTILISER_ACTIVE.YES, DAIRY_GRASSLAND_FERTILISER_ACTIVE.NO].map((opt) => ({
                    label: t(`dairyFeedPastureForm.grasslandFertiliser.options.${opt}`),
                    value: opt,
                  }))}
                />
              </Flex>

              {grasslandFertiliserActive === DAIRY_GRASSLAND_FERTILISER_ACTIVE.YES &&
                keyedGrasslandFertilisers.entries.map((fertiliser, index) => {
                  const selectGroupWithIndex = `footprinting-dairy-grassland-fertiliser-select-${index}`;

                  return (
                    <Form.Item key={fertiliser.clientKey} className="padding-left-100 padding-right-100 padding-bottom-0 padding-top-75">
                      <Flex vertical gap={16}>
                        <Flex justify="space-between" align="center" vertical={false}>
                          <Typography variant="h5" style={{ flex: 1 }}>
                            {t("dairyFeedPastureForm.grasslandFertiliser.entryTitle", { count: index + 1 })}
                          </Typography>
                          <IconButton
                            type="tertiary"
                            disabled={readOnly}
                            dataTestId={`delete-grassland-fertiliser-${index}`}
                            size="small"
                            iconName="tabler/trash"
                            onClick={() => {
                              keyedGrasslandFertilisers.actions.remove(fertiliser.clientKey);
                              if (keyedGrasslandFertilisers.entries.length === 1) setGrasslandFertiliserActive(null);
                            }}
                          />
                        </Flex>
                        <Select
                          disabled={readOnly}
                          name={`footprinting-dairy-grassland-fertiliser-select-${fertiliser.clientKey}`}
                          dataTestId={selectGroupWithIndex}
                          label={t("dairyFeedPastureForm.grasslandFertiliser.productLabel")}
                          rules={[{ required: true, message: "" }]}
                          initialValue={fertiliser.fertiliserProductId || undefined}
                          value={fertiliser.fertiliserProductId || undefined}
                          onChange={(v) => keyedGrasslandFertilisers.actions.update(fertiliser.clientKey, { fertiliserProductId: v })}
                          dropdownStyle={{ zIndex: 10010 }} // The modal presenting us has a z-index of 10k
                          showSearch
                        >
                          {(mineralFertilizerDataQuery?.data ?? [])
                            .filter(({ id }) => id !== ZERO_UUID)
                            .map((product) => (
                              <Option
                                disabled={readOnly}
                                key={`${selectGroupWithIndex}-mineral-${product.id}`}
                                data-testid={`${selectGroupWithIndex}-mineral-${product.id}`}
                                value={product.id}
                              >
                                {t(`${PRACTICE_SLUG.MINERAL_FERTILIZERS}.${product.translationSlug}`, { ns: "practices" })}
                              </Option>
                            ))}
                          {(organicFertilizerDataQuery?.data ?? [])
                            .filter(({ id }) => id !== ZERO_UUID)
                            .map((product) => (
                              <Option
                                disabled={readOnly}
                                key={`${selectGroupWithIndex}-organic-${product.id}`}
                                data-testid={`${selectGroupWithIndex}-organic-${product.id}`}
                                value={product.id}
                              >
                                {t(`${PRACTICE_SLUG.ORGANIC_FERTILIZERS}.${product.translationSlug}`, { ns: "practices" })}
                              </Option>
                            ))}
                        </Select>

                        <InputNumberWithUnitSelector
                          key={`amount-${fertiliser.clientKey}-${fertiliser.fertiliserProductId}`}
                          className="floating-label"
                          disabled={readOnly}
                          name={`grassland-fertiliser-amount-${fertiliser.clientKey}`}
                          dataTestId={`footprinting-dairy-grassland-fertiliser-amount-input-${index}`}
                          label={t("dairyFeedPastureForm.grasslandFertiliser.amountLabel")}
                          unitLabel={t("unit", { ns: "footprinting" })}
                          units={["tonnes", "kilograms", "cubic_meters", "liters"]}
                          value={fertiliser.totalAmountApplied}
                          initialValue={fertiliser.totalAmountApplied}
                          unitValue={fertiliser.totalAmountUnit}
                          onChange={(v, unit) =>
                            keyedGrasslandFertilisers.actions.update(fertiliser.clientKey, {
                              totalAmountApplied: v,
                              totalAmountUnit: unit as ENUM_FERTILIZER_UNITS,
                            })
                          }
                          validateFirst
                          rules={[
                            {
                              required: true,
                              validator: (_: unknown, v: number) => {
                                if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                                  return Promise.reject(new Error(t("dairyFeedPastureForm.grasslandFertiliser.amountError")));
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                        />
                      </Flex>
                    </Form.Item>
                  );
                })}

              {grasslandFertiliserActive === DAIRY_GRASSLAND_FERTILISER_ACTIVE.YES && (
                <Button
                  type="text"
                  dataTestId="add-grassland-fertiliser-button"
                  disabled={readOnly}
                  onClick={() => {
                    keyedGrasslandFertilisers.actions.add(grasslandFertiliserPlaceholder);
                  }}
                >
                  <Icon iconName="klim/plus" className="icon inline" /> {t("dairyFeedPastureForm.grasslandFertiliser.add")}
                </Button>
              )}
            </Flex>
          </Form>
        </Card>
      )}
    </Flex>
  );
};

FeedPastureForm.Key = "dairy.feedPasture";
export { FeedPastureForm };
